/**
 * Compass Calibration Service for FlowRide
 *
 * GPS-based automatic calibration is disabled; only manual calibration runs.
 *
 * Manual calibration:
 * - User taps position marker
 * - First tap: aligns to nearest road (or map north if no road)
 * - Second tap: rotates 180°
 * - Only works below 5 km/h
 */

import { CalibrationMode, CalibrationState, CompassData, FilteredGpsData, NavigationEventType, type NavigationEvent } from "../../types/Navigation";
import { filteredGpsDataService } from "./FilteredGpsDataService";
import { rawCompassDataService } from "./RawCompassDataService";

/**
 * Event listener callback type
 */
type CalibrationEventListener = ( state: CalibrationState ) => void;

/**
 * Service that manages compass calibration
 */
export class CompassCalibrationService {

    /** Singleton instance */
    private static instance: CompassCalibrationService | null = null;

    /** Current calibration state */
    private static currentState: CalibrationState = {
        "mode":            CalibrationMode.NONE,
        "offset":          0,
        "active":          false,
        "lastCalibration": null,
        "sampleCount":     0,
    };

    /** Event listeners */
    private listeners: Set<CalibrationEventListener> = new Set();

    /** Whether the service is active */
    private active: boolean = false;

    /** Unsubscribe functions */
    private unsubscribeFromGps: ( () => void ) | null = null;

    private unsubscribeFromCompass: ( () => void ) | null = null;

    /** GPS data history for automatic calibration */
    private gpsHistory: Array<{
        "speed":     number;
        "heading":   number;
        "timestamp": number;
    }> = [];

    /** Compass data history */
    private compassHistory: Array<{
        "heading":   number;
        "timestamp": number;
    }> = [];

    /** When false, GPS history and {@link checkAutomaticCalibration} are skipped. */
    private static readonly ENABLE_AUTOMATIC_CALIBRATION = false;

    /** Speed threshold for automatic calibration (m/s) */
    private readonly SPEED_THRESHOLD = 5 / 3.6; // 5 km/h in m/s

    /** Minimum duration at speed for calibration (ms) */
    private readonly MIN_DURATION_AT_SPEED = 15000; // 15 seconds

    /** Calibration re-evaluation interval (ms) */
    private readonly CALIBRATION_INTERVAL = 60000; // 1 minute

    /** Reverse movement threshold (degrees) */
    private readonly REVERSE_THRESHOLD = 100;

    /** Speed threshold for manual calibration (m/s) */
    private readonly MANUAL_SPEED_THRESHOLD = 5 / 3.6; // 5 km/h in m/s

    /** Maximum history size */
    private readonly MAX_HISTORY_SIZE = 100;

    /** Last calibration check timestamp */
    private lastCalibrationCheck: number = 0;

    /** Manual calibration tap count */
    private manualTapCount: number = 0;

    /** Manual calibration bearing (from nearest road) */
    private manualBearing: number | null = null;

    /** Manual calibration timeout */
    private manualTimeout: number | null = null;

    /** Current GPS data */
    private currentGpsData: FilteredGpsData | null = null;

    /** Current compass data */
    private currentCompassData: CompassData | null = null;

    /**
     * Private constructor for singleton pattern
     */
    private constructor() {
    // Private constructor
    }

    /**
     * Get singleton instance
     * @returns Service instance
     */
    public static getInstance(): CompassCalibrationService {
        if ( CompassCalibrationService.instance === null ) {
            CompassCalibrationService.instance = new CompassCalibrationService();
        }

        return CompassCalibrationService.instance;
    }

    /**
     * Get current calibration state
     * @returns Current calibration state
     */
    public static getCalibrationState(): CalibrationState {
        return { ...CompassCalibrationService.currentState };
    }

    /**
     * Start calibration service
     */
    public start(): void {
        if ( this.active ) {
            console.warn( "[CompassCalibrationService] Already active - skipping start" );

            return;
        }

        this.active = true;

        // Subscribe to GPS and compass data
        this.unsubscribeFromGps = filteredGpsDataService.subscribe( this.handleGpsData.bind( this ) );
        this.unsubscribeFromCompass = rawCompassDataService.subscribe( this.handleCompassData.bind( this ) );

        // console.log( "[CompassCalibrationService] Started" );
    }

    /**
     * Stop calibration service
     */
    public stop(): void {
        if ( this.unsubscribeFromGps !== null ) {
            this.unsubscribeFromGps();
            this.unsubscribeFromGps = null;
        }

        if ( this.unsubscribeFromCompass !== null ) {
            this.unsubscribeFromCompass();
            this.unsubscribeFromCompass = null;
        }

        this.active = false;
        this.gpsHistory = [];
        this.compassHistory = [];

        // console.log( "[CompassCalibrationService] Stopped" );
    }

    /**
     * Subscribe to calibration state changes
     * @param listener - Callback function
     * @returns Unsubscribe function
     */
    public subscribe( listener: CalibrationEventListener ): () => void {
        this.listeners.add( listener );

        // Immediately send current state
        listener( CompassCalibrationService.currentState );

        return () => {
            this.listeners.delete( listener );
        };
    }

    /**
     * Handle GPS data
     * @param gpsData - Filtered GPS data
     */
    private handleGpsData( gpsData: FilteredGpsData ): void {
        this.currentGpsData = gpsData;

        if ( CompassCalibrationService.ENABLE_AUTOMATIC_CALIBRATION ) {
            if ( gpsData.speed !== null && gpsData.heading !== null ) {
                this.gpsHistory.push( {
                    "speed":     gpsData.speed,
                    "heading":   gpsData.heading,
                    "timestamp": gpsData.timestamp,
                } );

                if ( this.gpsHistory.length > this.MAX_HISTORY_SIZE ) {
                    this.gpsHistory.shift();
                }
            }

            this.checkAutomaticCalibration();
        }
    }

    /**
     * Handle compass data
     * @param compassData - Compass data
     */
    private handleCompassData( compassData: CompassData ): void {
        this.currentCompassData = compassData;

        // Add to history
        this.compassHistory.push( {
            "heading":   compassData.heading,
            "timestamp": compassData.timestamp,
        } );

        // Trim history
        if ( this.compassHistory.length > this.MAX_HISTORY_SIZE ) {
            this.compassHistory.shift();
        }
    }

    /**
     * Check for automatic calibration conditions
     */
    private checkAutomaticCalibration(): void {
        if ( !CompassCalibrationService.ENABLE_AUTOMATIC_CALIBRATION ) {
            return;
        }

        const now = Date.now();

        // Check if enough time has passed since last calibration
        if ( now - this.lastCalibrationCheck < this.CALIBRATION_INTERVAL ) {
            return;
        }

        // Check if we have enough history
        if ( this.gpsHistory.length < 10 ) {
            return;
        }

        // Find samples in the last 15 seconds
        const recentSamples = this.gpsHistory.filter( ( sample ) => now - sample.timestamp < this.MIN_DURATION_AT_SPEED );

        // Check if all recent samples are above speed threshold
        const allAboveThreshold = recentSamples.every( ( sample ) => sample.speed > this.SPEED_THRESHOLD );

        if ( allAboveThreshold && recentSamples.length >= 5 ) {
            // Calculate average GPS heading
            const avgGpsHeading = this.calculateCircularMean( recentSamples.map( ( s ) => s.heading ) );

            // Get current compass heading (before calibration)
            const currentCompassHeading = this.getUncalibratedCompassHeading();

            if ( avgGpsHeading !== null && currentCompassHeading !== null ) {
                // Calculate offset
                let offset = avgGpsHeading - currentCompassHeading;

                // Check for reverse movement
                if ( Math.abs( offset ) > this.REVERSE_THRESHOLD ) {
                    // User is moving in reverse - adjust by 180°
                    offset = this.normalizeAngle( offset + 180 );

                    // console.log( "[CompassCalibrationService] Reverse movement detected" );
                }

                // Normalize offset
                offset = this.normalizeAngle( offset );

                // Apply calibration
                this.applyAutomaticCalibration( offset );

                this.lastCalibrationCheck = now;
            }
        }
    }

    /**
     * Get uncalibrated compass heading
     * @returns Uncalibrated heading or null
     */
    private getUncalibratedCompassHeading(): number | null {
        // Try to use current compass data first
        let compassData = this.currentCompassData;

        // Fall back to last value from service if current data is not available
        if ( compassData === null ) {
            const lastValue = rawCompassDataService.getLastValue();

            // Check if we have valid compass data (timestamp > 0 means data was received)
            if ( lastValue.timestamp === 0 ) {
                // No compass data received yet
                console.warn( "[CompassCalibrationService] No compass data available for calibration" );

                return null;
            }
            compassData = lastValue;
        }

        // Remove current calibration to get raw heading
        return this.normalizeAngle( compassData.heading - compassData.offset );
    }

    /**
     * Apply automatic calibration
     * @param offset - Calibration offset in degrees
     */
    private applyAutomaticCalibration( offset: number ): void {
        // console.log( `[CompassCalibrationService] Automatic calibration: offset=${ offset.toFixed( 1 ) }°` );

        // Update state
        CompassCalibrationService.currentState = {
            "mode":            CalibrationMode.AUTOMATIC,
            offset,
            "active":          true,
            "lastCalibration": Date.now(),
            "sampleCount":     this.gpsHistory.length,
        };

        // Apply to compass service
        rawCompassDataService.setCalibrationOffset( offset );

        // Emit state change
        this.emitStateChange();
    }

    /**
     * Trigger manual calibration
     * @param nearestRoadBearing - Bearing of nearest road (optional)
     * @returns True if calibration was applied
     */
    public triggerManualCalibration( nearestRoadBearing?: number ): boolean {
    // Check if speed is below threshold
        if ( this.currentGpsData !== null &&
          this.currentGpsData.speed !== null &&
          this.currentGpsData.speed > this.MANUAL_SPEED_THRESHOLD ) {
            console.warn( "[CompassCalibrationService] Speed too high for manual calibration" );

            return false;
        }

        // Clear existing manual timeout
        if ( this.manualTimeout !== null ) {
            window.clearTimeout( this.manualTimeout );
        }

        this.manualTapCount++;

        if ( this.manualTapCount === 1 ) {
            // First tap: align to nearest road
            if ( nearestRoadBearing === undefined ) {
                console.warn( "[CompassCalibrationService] No road bearing provided for first tap" );
                this.resetManualCalibration();

                return false;
            }

            this.manualBearing = nearestRoadBearing;

            // Get current uncalibrated compass heading
            const compassHeading = this.getUncalibratedCompassHeading();
            if ( compassHeading === null ) {
                console.warn( "[CompassCalibrationService] Cannot get uncalibrated compass heading for manual calibration", {
                    "currentCompassData": this.currentCompassData,
                    "lastCompassValue":   rawCompassDataService.getLastValue(),
                } );
                this.resetManualCalibration();

                return false;
            }

            /*
             * Map layer uses icon-rotate = -bearing, so we pass bearing = 360 - geographic
             * for the cone to point in the geographic direction. Align stored heading to that.
             */
            const roadBearingCorrected = this.normalizeAngle( 360 - nearestRoadBearing );
            const offset = this.normalizeAngle( roadBearingCorrected - compassHeading );

            this.applyManualCalibration( offset );

            // Set timeout to reset tap count
            this.manualTimeout = window.setTimeout( () => {
                this.resetManualCalibration();
            }, 5000 ); // 5 seconds to tap again

            // console.log( `[CompassCalibrationService] Manual calibration (1st tap): aligned to ${ nearestRoadBearing.toFixed( 1 ) }°` );

            return true;
        } else if ( this.manualTapCount === 2 ) {
            // Second tap: rotate 180°
            const currentOffset = CompassCalibrationService.currentState.offset;
            const newOffset = this.normalizeAngle( currentOffset + 180 );

            this.applyManualCalibration( newOffset );

            this.resetManualCalibration();

            // console.log( "[CompassCalibrationService] Manual calibration (2nd tap): rotated 180°" );

            return true;
        }

        return false;
    }

    /**
     * Apply manual calibration
     * @param offset - Calibration offset in degrees
     */
    private applyManualCalibration( offset: number ): void {
    // Update state
        CompassCalibrationService.currentState = {
            "mode":            CalibrationMode.MANUAL,
            offset,
            "active":          true,
            "lastCalibration": Date.now(),
            "sampleCount":     0,
        };

        // Apply to compass service
        rawCompassDataService.setCalibrationOffset( offset );

        // Emit state change
        this.emitStateChange();
    }

    /**
     * Reset manual calibration state
     */
    private resetManualCalibration(): void {
        this.manualTapCount = 0;
        this.manualBearing = null;

        if ( this.manualTimeout !== null ) {
            window.clearTimeout( this.manualTimeout );
            this.manualTimeout = null;
        }
    }

    /**
     * Clear all calibration
     */
    public clearCalibration(): void {
        CompassCalibrationService.currentState = {
            "mode":            CalibrationMode.NONE,
            "offset":          0,
            "active":          false,
            "lastCalibration": null,
            "sampleCount":     0,
        };

        rawCompassDataService.clearCalibration();
        this.resetManualCalibration();

        // console.log( "[CompassCalibrationService] Calibration cleared" );
        this.emitStateChange();
    }

    /**
     * Calculate circular mean of angles
     * @param angles - Array of angles in degrees
     * @returns Mean angle or null if empty
     */
    private calculateCircularMean( angles: number[] ): number | null {
        if ( angles.length === 0 ) {
            return null;
        }

        let sumSin = 0;
        let sumCos = 0;

        for ( const angle of angles ) {
            const rad = angle * Math.PI / 180;
            sumSin += Math.sin( rad );
            sumCos += Math.cos( rad );
        }

        const meanRad = Math.atan2( sumSin / angles.length, sumCos / angles.length );

        return this.normalizeAngle( meanRad * 180 / Math.PI );
    }

    /**
     * Normalize angle to [0, 360)
     * @param angle - Angle in degrees
     * @returns Normalized angle
     */
    private normalizeAngle( angle: number ): number {
        return ( ( angle % 360 ) + 360 ) % 360;
    }

    /**
     * Emit state change to listeners
     */
    private emitStateChange(): void {
        const event: NavigationEvent<CalibrationState> = {
            "type":      NavigationEventType.COMPASS_CALIBRATION,
            "data":      CompassCalibrationService.currentState,
            "timestamp": Date.now(),
        };

        this.listeners.forEach( ( listener ) => {
            try {
                listener( event.data );
            } catch( error ) {
                console.error( "[CompassCalibrationService] Error in listener:", error );
            }
        } );
    }

    /**
     * Check if service is active
     * @returns True if active
     */
    public isActive(): boolean {
        return this.active;
    }

    /**
     * Get number of active listeners
     * @returns Number of listeners
     */
    public getListenerCount(): number {
        return this.listeners.size;
    }

    /**
     * Get manual tap count (for UI feedback)
     * @returns Current tap count
     */
    public getManualTapCount(): number {
        return this.manualTapCount;
    }

}

/**
 * Export singleton instance for convenience
 */
export const compassCalibrationService = CompassCalibrationService.getInstance();

